import { useCallback, useEffect, useState, type ReactNode } from 'react'
import type { BoutiqueDTO, MarcheDTO, OffreDTO, OrdreDTO, Stat } from '@whatremains/shared'
import { EffetObjet, NomObjet } from '../composants/NomObjet'
import { api, ErreurApi } from '../api'
import { IconeObjet } from '../composants/IconeObjet'
import { TypeObjet } from '../composants/TypeObjet'
import { Atelier } from '../composants/Atelier'
import { Echange } from '../composants/Comparaison'

/**
 * Le négoce : l'échoppe du lieu, et le marché.
 *
 * Les deux vivent sur le même écran parce qu'ils répondent à la même question,
 * « qu'est-ce que je fais de ça », et que les comparer demande de les voir
 * ensemble. Le marchand paie tout de suite et mal ; le marché paie mieux, plus
 * tard, et peut ne rien payer. Tout l'écran existe pour rendre cet arbitrage
 * lisible sans qu'on ait à le calculer.
 *
 * `08-INTERFACE.md` §9 : aucun glisser-déposer, aucune grille à réorganiser,
 * et le total avant de confirmer.
 */

const NOMS_STATS: Record<Stat, string> = {
  force: 'For', adresse: 'Adr', esprit: 'Esp', vigueur: 'Vig', fortune: 'Ftn',
}

const NOMS_ACCUEIL: Record<BoutiqueDTO['accueil'], string> = {
  hostile: 'hostile', mefiant: 'méfiant', neutre: 'neutre',
  favorable: 'favorable', devoue: 'dévoué',
}

function bonusEnLigne(bonus: Partial<Record<Stat, number>>): string {
  const lignes = Object.entries(bonus).filter(([, v]) => v)
  if (lignes.length === 0) return ''
  return lignes.map(([s, v]) => `${NOMS_STATS[s as Stat]} +${v}`).join(' · ')
}

/**
 * Combien on en vend, quand on en a plusieurs.
 *
 * Enzo : « que je puisse au marché ou à l'échoppe vendre par quantité que je
 * choisis. » Un champ libre obligerait à valider une saisie ; deux flèches et
 * un « tout » couvrent le geste réel, qui est presque toujours « un » ou
 * « tout ». Le nombre reste sélectionnable au clavier par les deux boutons.
 *
 * N'apparaît pas quand il n'y en a qu'un : un sélecteur qui ne sélectionne rien
 * est du bruit.
 */
function ChoixQuantite({
  valeur,
  max,
  surChangement,
}: {
  valeur: number
  max: number
  surChangement: (n: number) => void
}) {
  if (max <= 1) return null
  const borne = (n: number) => Math.max(1, Math.min(max, n))
  return (
    <span className="quantite">
      <button
        className="discret"
        disabled={valeur <= 1}
        aria-label="un de moins"
        onClick={() => surChangement(borne(valeur - 1))}
      >
        −
      </button>
      <b aria-live="polite">{valeur}</b>
      <span className="tenu">sur {max}</span>
      <button
        className="discret"
        disabled={valeur >= max}
        aria-label="un de plus"
        onClick={() => surChangement(borne(valeur + 1))}
      >
        +
      </button>
      <button className="discret" disabled={valeur >= max} onClick={() => surChangement(max)}>
        tout
      </button>
    </span>
  )
}

/**
 * Une ligne d'ordre : ce qu'on a mis en vente, et à quel prix.
 *
 * Commune aux quatre blocs (vendu, invendu, en cours, passé) pour que la même
 * information se lise toujours au même endroit. Seul le geste change, et il
 * arrive en `children`.
 */
function LigneOrdre({ o, children }: { o: OrdreDTO; children?: ReactNode }) {
  return (
    <div className="paire paire-ordre">
      <span className="paire-glyphe">
        <IconeObjet
          objet={{
            code: o.code, famille: o.famille, palier: o.palier,
            materiau: '', categorie: o.categorie,
          }}
          taille={26}
        />
      </span>
      <NomObjet
        o={o}
        suffixe={
          <>
            {o.quantite > 1 && <span className="tenu"> ×{o.quantite}</span>}
            <TypeObjet o={o} className="objet-type objet-type-ligne" />
          </>
        }
      />
      <span className="chiffre">
        {o.prixDemande} or
        {o.quantite > 1 && <span className="tenu"> la pièce</span>}
        {o.quantite > 1 && <b className="total"> · {o.prixDemande * o.quantite} or</b>}
        <span className="tenu"> · annoncé à {o.chanceAnnoncee} %</span>
      </span>
      <span className="ordre-etat">
        {o.etat === 'conclu' && <em className="gain">pris pour {o.prixObtenu} or</em>}
        {o.etat === 'sans_preneur' && 'personne n’en a voulu'}
        {o.etat === 'releve' &&
          (o.prixObtenu && o.prixObtenu > 0
            ? <em className="gain">vendu {o.prixObtenu} or</em>
            : 'repris')}
      </span>
      {children}
    </div>
  )
}

function Offre({
  o,
  action,
  surAction,
}: {
  o: OffreDTO
  action: string
  surAction: (o: OffreDTO) => void
}) {
  return (
    <div className="offre">
      {/*
        L'échoppe vend un modèle, pas un exemplaire : `OffreDTO` ne porte pas de
        matériau. La silhouette prend donc la teinte neutre, ce qui dit ce qui
        est vrai plutôt que d'inventer une matière. Le cadre, lui, est complet.
      */}
      <span className="offre-glyphe">
        {/*
          La CATEGORIE manquait, et c'est tout ce qui manquait.

          `IconeObjet` refuse l'icone peinte d'une famille quand la piece n'est
          pas de l'equipement : « Ferraille » est rangee en famille `masse` et
          prenait sinon l'icone d'une masse. Mais le marche ne transmettait
          aucune categorie, donc la garde se declenchait pour TOUT, y compris
          l'equipement qui a pourtant son image. Enzo : « je ne sais pas
          pourquoi les icones ne sont pas affichees sur le market. »
        */}
        <IconeObjet
          objet={{
            code: o.code, famille: o.famille, palier: o.palier,
            materiau: '', categorie: o.categorie,
          }}
          taille={34}
        />
      </span>

      <div className="offre-corps">
        <NomObjet o={o} />
        {o.description && <i>{o.description}</i>}
        {/* Ce que la piece EST, avant ce qu'elle vaut. */}
        <TypeObjet o={o} />
        <span className="offre-chiffres">
          niveau {o.niveauRequis}
          {o.degatsMax > 0 && ` · ${o.degatsMin} à ${o.degatsMax} de dégâts`}
          {bonusEnLigne(o.bonus) && ` · ${bonusEnLigne(o.bonus)}`}
          {o.horsDePortee && <em className="avertissement"> · trop haut pour toi</em>}
        </span>
        {/*
          Ce qu'un consommable FAIT. Sans cette ligne, sa fiche se resumait a un
          nom et a un niveau : « aucune idee sur ce a quoi ca sert ».
        */}
        {o.effetTexte && <span className="offre-effet">{o.effetTexte}</span>}
        {/*
          Ce que ça change, comparé à ce qu'on porte déjà. Sans cette ligne, les
          bonus affichés au-dessus ne renseignent sur rien : +3 de force peut
          être un recul si la pièce en place en donne 5.
        */}
        <Echange echange={o.echange} />
      </div>

      <div className="offre-prix">
        <b className={o.tropCher ? 'tenu' : undefined}>{o.prix} or</b>
        {/*
          Ce qu'il en reste. Une pièce d'équipement est une trouvaille et n'en
          tient qu'une, donc rien à dire ; une provision se vend au stock, et
          sans ce nombre on achète un onguent en croyant vider l'étagère.
        */}
        {o.restant > 1 && <span className="offre-restant">{o.restant} en rayon</span>}
        <button className="discret" disabled={o.tropCher} onClick={() => surAction(o)}>
          {o.tropCher ? 'trop cher' : action}
        </button>
      </div>
    </div>
  )
}

export function Negoce({ id, surChangement }: { id: string; surChangement: () => void }) {
  const [onglet, setOnglet] = useState<'echoppe' | 'marche' | 'atelier'>('echoppe')
  const [boutique, setBoutique] = useState<BoutiqueDTO | null>(null)
  const [marche, setMarche] = useState<MarcheDTO | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  // Le dépôt en préparation : un lot, un prix UNITAIRE, une durée, une quantité.
  const [depot, setDepot] =
    useState<{ objetId: string; prix: number; duree: number; quantite: number } | null>(null)
  /*
   * Combien on vend de chaque lot au marchand, tant qu'on n'a pas cliqué.
   *
   * Gardé par identifiant de tête de lot plutôt que dans un seul état : on peut
   * régler deux lignes avant d'en valider une, et remettre à zéro la ligne
   * vendue sans toucher aux autres.
   */
  const [quantites, setQuantites] = useState<Record<string, number>>({})
  const quantiteDe = (objetId: string, max: number) =>
    Math.max(1, Math.min(max, quantites[objetId] ?? 1))
  /*
   * Ce que le dépôt donnerait, pour les trois durées, calculé par le serveur.
   *
   * Enzo : « il n'y a écrit nulle part ce que ça change de laisser l'ordre plus
   * ou moins longtemps. On ne peut pas deviner les mécaniques de jeu. » La
   * chance de trouver preneur n'était annoncée qu'en réponse au dépôt, donc
   * après avoir décidé et payé les frais.
   */
  const [estimation, setEstimation] = useState<
    { frais: number; total: number; chances: { minutes: number; chance: number }[] } | null
  >(null)

  const recharger = useCallback(async () => {
    const [b, m] = await Promise.all([api.boutique(id), api.marche(id)])
    setBoutique(b)
    setMarche(m)
  }, [id])

  useEffect(() => { void recharger() }, [recharger])

  /*
   * Recalcul à chaque mouvement du curseur de prix.
   *
   * Sans temporisation : la route ne fait qu'une lecture en mémoire et un
   * calcul, et un délai rendrait le chiffre en retard sur la poignée qu'on
   * tient, ce qui est pire que pas de chiffre du tout.
   */
  useEffect(() => {
    if (!depot) { setEstimation(null); return }
    let vivant = true
    void api.estimerDepot(id, depot.objetId, depot.prix, depot.quantite)
      .then((e) => { if (vivant) setEstimation(e) })
      .catch(() => { if (vivant) setEstimation(null) })
    return () => { vivant = false }
  }, [id, depot])

  async function agir(action: () => Promise<unknown>, dire?: (r: unknown) => string) {
    setErreur(null)
    try {
      const r = await action()
      if (dire) setNote(dire(r))
      await recharger()
      surChangement()
    } catch (e) {
      setNote(null)
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  if (!boutique || !marche) {
    return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>
  }

  const enPreparation = depot
    ? marche.deposables.find((d) => d.objetId === depot.objetId)
    : undefined

  // Ce qui demande un geste d'abord, ce qui court ensuite. Les invendus sont
  // groupés entre eux, comme demandé, plutôt que mêlés aux ordres qui tournent.
  const vendus = marche.ordres.filter((o) => o.etat === 'conclu')
  const invendus = marche.ordres.filter((o) => o.etat === 'sans_preneur')
  const enCours = marche.ordres.filter((o) => o.etat === 'en_cours')

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">{boutique.lieu}</div>
        <h1>Négoce</h1>
        <p className="attenue" style={{ maxWidth: '60ch' }}>
          On te reçoit en <b>{NOMS_ACCUEIL[boutique.accueil]}</b>. {boutique.effetAccueil}
        </p>
        {erreur && <p className="erreur">{erreur}</p>}
        {note && <p className="note">{note}</p>}

        <div className="bascule">
          <button aria-pressed={onglet === 'echoppe'} onClick={() => setOnglet('echoppe')}>
            Échoppe
          </button>
          <button aria-pressed={onglet === 'marche'} onClick={() => setOnglet('marche')}>
            Marché{marche.ordres.some((o) => o.relevable) ? ' ·' : ''}
          </button>
          <button aria-pressed={onglet === 'atelier'} onClick={() => setOnglet('atelier')}>
            Atelier
          </button>
        </div>
      </div>

      {onglet === 'atelier' && <Atelier id={id} surChangement={surChangement} />}

      {onglet === 'echoppe' && !boutique.ouvert && (
        <section>
          <p className="attenue">
            On ne te sert pas ici. Ça peut changer, si ce qu’on pense de toi change.
          </p>
        </section>
      )}

      {onglet === 'echoppe' && boutique.ouvert && (
        <>
          <section>
            <div className="surtitre">En rayon aujourd’hui</div>
            {/*
              Pourquoi le rayon est à CE niveau-là, et quand il change.

              Enzo, niveau 11 : « ce qui m'est proposé à l'échoppe sont des
              items trop bas niveau, 1, 4, 6, 8. Je ne sais pas si c'est voulu
              et normal ? » Ça l'est : un marchand tient la marchandise de SA
              région, pas la tienne. Mais rien ne le disait, donc ça se lisait
              comme un rayon cassé au lieu d'une raison d'aller ailleurs.
            */}
            <p className="attenue" style={{ maxWidth: '64ch' }}>
              On tient ici la marchandise du pays, taillée pour le{' '}
              <b>niveau {boutique.niveauRayon}</b>, quel que soit le tien. Pour du matériel plus
              haut, il faut monter plus haut. Le rayon change dans{' '}
              {boutique.renouvelleDans >= 60
                ? `${Math.round(boutique.renouvelleDans / 60)} h`
                : `${boutique.renouvelleDans} min`}
              .
            </p>
            {boutique.offres.length === 0 ? (
              <p className="tenu">Le rayon est vide.</p>
            ) : (
              <div className="offres">
                {boutique.offres.map((o) => (
                  <Offre
                    key={o.id}
                    o={o}
                    action="acheter"
                    surAction={(x) =>
                      void agir(
                        () => api.acheter(id, x.id),
                        (r) => `${x.nom} acheté ${(r as { prix: number }).prix} or.`,
                      )
                    }
                  />
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="surtitre">
              Ce qu’il te rachète · {Math.round(boutique.partRachat * 100)} % de la valeur
            </div>
            <p className="attenue" style={{ maxWidth: '62ch' }}>
              Tout de suite et sans risque. Le marché paie mieux, mais il fait attendre et il
              arrive qu’il ne paie pas.
            </p>
            {boutique.rachats.length === 0 ? (
              <p className="tenu">Tu n’as rien à vendre.</p>
            ) : (
              <div className="paires">
                {boutique.rachats.map((r) => {
                  const n = quantiteDe(r.objetId, r.quantite)
                  return (
                    <div className="paire paire-rachat" key={r.objetId}>
                      <span className="paire-glyphe">
                        <IconeObjet
                          objet={{
                            code: r.code, famille: r.famille, palier: r.palier,
                            materiau: r.materiau, categorie: r.categorie,
                          }}
                          taille={26}
                        />
                      </span>
                      <NomObjet
                        o={r}
                        suffixe={
                          <>
                            {r.quantite > 1 && <span className="tenu"> ×{r.quantite}</span>}
                            <TypeObjet o={r} className="objet-type objet-type-ligne" />
                            <EffetObjet effet={r.effetTexte} />
                          </>
                        }
                      />
                      <span className="chiffre">
                        {r.prixRachat} or
                        <span className="tenu"> la pièce, sur {r.valeurMarchande}</span>
                        {/* Le total, avant de confirmer. `08-INTERFACE.md` §9. */}
                        {n > 1 && <b className="total"> · {r.prixRachat * n} or en tout</b>}
                      </span>
                      <ChoixQuantite
                        valeur={n}
                        max={r.quantite}
                        surChangement={(v) => setQuantites((q) => ({ ...q, [r.objetId]: v }))}
                      />
                      <button
                        className="discret"
                        onClick={() =>
                          void agir(
                            () => api.vendre(id, r.objetId, n),
                            (x) => {
                              const v = x as { prix: number; quantite: number }
                              setQuantites((q) => ({ ...q, [r.objetId]: 1 }))
                              return v.quantite > 1
                                ? `${v.quantite} ${r.nom} vendus ${v.prix} or.`
                                : `${r.nom} vendu ${v.prix} or.`
                            },
                          )
                        }
                      >
                        vendre
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </section>
        </>
      )}

      {onglet === 'marche' && (
        <>
          {/*
            Les ordres, en trois blocs, et c'est tout le sujet.

            Enzo : « les ordres de vente qui sont passés restent là
            indéfiniment. Idéalement il faudrait qu'ils sortent de mes ordres
            pour passer dans une liste dépliante ailleurs. Mais je ne veux pas
            que ce soit toujours là. Ensuite il faudrait aussi que mes items non
            vendus soient tous rangés ensemble. Et que j'aie un bouton pour
            relancer directement la même offre. »

            D'où l'ordre de lecture : ce qui demande un geste d'abord, ce qui
            court ensuite, ce qui est soldé replié en bas. Les invendus forment
            leur propre bloc à l'intérieur du premier, avec les deux gestes.
          */}
          {vendus.length > 0 && (
            <section>
              <div className="surtitre">Vendu · à encaisser</div>
              <div className="paires">
                {vendus.map((o) => (
                  <LigneOrdre key={o.id} o={o}>
                    <button
                      className="action"
                      onClick={() =>
                        void agir(
                          () => api.releverOrdre(id, o.id),
                          (r) => `${o.nom} vendu ${(r as { or: number }).or} or.`,
                        )
                      }
                    >
                      encaisser
                    </button>
                  </LigneOrdre>
                ))}
              </div>
            </section>
          )}

          {invendus.length > 0 && (
            <section>
              <div className="surtitre">Personne n’en a voulu</div>
              <p className="attenue" style={{ maxWidth: '62ch' }}>
                Reprends-les dans tes sacs, ou remets-les en vente telles quelles. Les frais du
                nouveau dépôt sont dus, comme au premier.
              </p>
              <div className="paires">
                {invendus.map((o) => (
                  <LigneOrdre key={o.id} o={o}>
                    <span className="ordre-gestes">
                      <button
                        className="action"
                        onClick={() =>
                          void agir(
                            () => api.relancerOrdre(id, o.id),
                            (r) => {
                              const x = r as { frais: number; chance: number }
                              return `${o.nom} remis en vente. ${x.frais} or de frais, ${x.chance} % qu’il parte.`
                            },
                          )
                        }
                      >
                        remettre en vente
                      </button>
                      <button
                        className="discret"
                        onClick={() =>
                          void agir(
                            () => api.releverOrdre(id, o.id),
                            () => `${o.nom} t’est revenu.`,
                          )
                        }
                      >
                        reprendre
                      </button>
                    </span>
                  </LigneOrdre>
                ))}
              </div>
            </section>
          )}

          {enCours.length > 0 && (
            <section>
              <div className="surtitre">En cours</div>
              <div className="paires">
                {enCours.map((o) => (
                  <LigneOrdre key={o.id} o={o}>
                    <span className="tenu">rentre dans {o.minutesRestantes} min</span>
                  </LigneOrdre>
                ))}
              </div>
            </section>
          )}

          {marche.ordres.length === 0 && (
            <section>
              <div className="surtitre">Tes ordres</div>
              <p className="tenu">Tu n’as rien déposé.</p>
            </section>
          )}

          <section>
            <div className="surtitre">Déposer</div>
            <p className="attenue" style={{ maxWidth: '64ch' }}>
              Le prix se pose entre {Math.round(marche.partPlancher * 100)} % et{' '}
              {Math.round(marche.partPlafond * 100)} % de la valeur de la pièce. Plus tu vises
              haut, moins elle part. Le dépôt coûte {Math.round(marche.partFrais * 100)} % du prix
              demandé, et ces frais ne sont jamais rendus.
            </p>

            {marche.deposables.length === 0 ? (
              <p className="tenu">Tu n’as rien à déposer.</p>
            ) : (
              <div className="paires">
                {marche.deposables.map((d) => (
                  <div className="paire paire-depot" key={d.objetId}>
                    <span className="paire-glyphe">
                      <IconeObjet
                        objet={{
                          code: d.code, famille: d.famille, palier: d.palier,
                          materiau: d.materiau, categorie: d.categorie,
                        }}
                        taille={26}
                      />
                    </span>
                    <NomObjet
                      o={d}
                      suffixe={
                        <>
                          {d.quantite > 1 && <span className="tenu"> ×{d.quantite}</span>}
                          <TypeObjet o={d} className="objet-type objet-type-ligne" />
                          <EffetObjet effet={d.effetTexte} />
                        </>
                      }
                    />
                    <span className="chiffre">
                      valeur {d.valeurMarchande}
                      <span className="tenu"> · de {d.prixMin} à {d.prixMax}</span>
                    </span>
                    <button
                      className="discret"
                      onClick={() =>
                        setDepot(
                          depot?.objetId === d.objetId
                            ? null
                            : {
                                objetId: d.objetId,
                                prix: d.prixConseille,
                                duree: 240,
                                quantite: 1,
                              },
                        )
                      }
                      aria-pressed={depot?.objetId === d.objetId}
                    >
                      {depot?.objetId === d.objetId ? 'annuler' : 'déposer'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {depot && enPreparation && (
              <div className="preparation">
                <div className="surtitre">{enPreparation.nom}</div>

                {enPreparation.quantite > 1 && (
                  <div className="etiquette" style={{ marginBottom: 10 }}>
                    Combien tu en mets
                    <span className="tenu">
                      {' '}· le lot part entier ou pas du tout, à un prix à la pièce
                    </span>
                    <div style={{ marginTop: 6 }}>
                      <ChoixQuantite
                        valeur={depot.quantite}
                        max={enPreparation.quantite}
                        surChangement={(v) => setDepot({ ...depot, quantite: v })}
                      />
                    </div>
                  </div>
                )}

                <label>
                  <span className="etiquette">
                    Prix demandé · {depot.prix} or
                    {depot.quantite > 1 && <span className="tenu"> la pièce</span>}
                    <span className="tenu">
                      {' '}({Math.round((depot.prix / enPreparation.valeurMarchande) * 100)} % de
                      la valeur, frais {estimation?.frais ?? Math.max(1, Math.round(depot.prix * depot.quantite * marche.partFrais))} or)
                    </span>
                    {/* Le total avant de confirmer. `08-INTERFACE.md` §9. */}
                    {depot.quantite > 1 && (
                      <b className="total">
                        {' '}· {depot.prix * depot.quantite} or si tout part
                      </b>
                    )}
                  </span>
                  <input
                    type="range"
                    min={enPreparation.prixMin}
                    max={enPreparation.prixMax}
                    value={depot.prix}
                    onChange={(e) => setDepot({ ...depot, prix: Number(e.target.value) })}
                  />
                </label>

                <div className="etiquette">
                  Combien de temps tu le laisses
                  <span className="tenu">
                    {' '}· plus tu laisses longtemps, plus il a de chances de trouver preneur,
                    et plus tu attends ton or
                  </span>
                </div>
                <div className="bascule bascule-duree">
                  {marche.durees.map((d) => {
                    /*
                      Le chiffre qui arbitre, sur le bouton qui l'arbitre.

                      C'est la seule chose que la durée change, et elle n'était
                      écrite nulle part : les trois boutons se ressemblaient
                      exactement, à leur nom près.
                    */
                    const c = estimation?.chances.find((x) => x.minutes === d.minutes)
                    return (
                      <button
                        key={d.minutes}
                        aria-pressed={depot.duree === d.minutes}
                        onClick={() => setDepot({ ...depot, duree: d.minutes })}
                      >
                        {d.nom}
                        {c && <em className="duree-chance">{c.chance} % qu’il parte</em>}
                      </button>
                    )
                  })}
                </div>

                <p style={{ marginTop: 18 }}>
                  <button
                    className="action"
                    onClick={() =>
                      void agir(
                        () =>
                          api.deposer(
                            id, depot.objetId, depot.prix, depot.duree, depot.quantite,
                          ),
                        (r) => {
                          const x = r as { frais: number; chance: number; quantite: number }
                          setDepot(null)
                          const quoi = x.quantite > 1 ? `${x.quantite} pièces déposées` : 'Déposé'
                          return `${quoi}. ${x.frais} or de frais, ${x.chance} % de chance que ça parte.`
                        },
                      )
                    }
                  >
                    Déposer
                  </button>
                </p>
              </div>
            )}
          </section>

          {marche.ordresPasses.length > 0 && (
            <section>
              {/*
                Soldé, et rangé. « Je ne veux pas que ce soit toujours là. »
                Replié par défaut, mais pas jeté : savoir à combien on a vendu
                la dernière fois est ce qui permet de fixer le prix suivant.
              */}
              <details className="repli">
                <summary>
                  Ordres passés
                  <span className="tenu"> · {marche.ordresPasses.length}</span>
                </summary>
                <div className="paires">
                  {marche.ordresPasses.map((o) => (
                    <LigneOrdre key={o.id} o={o} />
                  ))}
                </div>
              </details>
            </section>
          )}

          <section>
            <div className="surtitre">À l’étal</div>
            <p className="attenue" style={{ maxWidth: '64ch' }}>
              Ce que d’autres ont mis en vente. Plus cher qu’en échoppe, et on y trouve ce qu’un
              marchand ne tient pas.
            </p>
            {marche.etal.length === 0 ? (
              <p className="tenu">L’étal est vide.</p>
            ) : (
              <div className="offres">
                {marche.etal.map((o) => (
                  <Offre
                    key={o.id}
                    o={o}
                    action="acheter"
                    surAction={(x) =>
                      void agir(
                        () => api.acheterEtal(id, x.id),
                        (r) => `${x.nom} acheté ${(r as { prix: number }).prix} or.`,
                      )
                    }
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}
